import { useCallback, useEffect, useState } from 'react';
import { api, type CoverageSettings } from '../api';
import AreaPicker, { type AreaValue } from '../components/AreaPicker';
import WatchAreasPanel from '../components/WatchAreasPanel';
import { ErrorBox, Loading, PageHeader } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { errorMessage, isAdmin } from '../lib/status';

const EMPTY: AreaValue = { area: null, boundary: null, center: null };

export default function Coverage() {
  const { me } = useLive();
  const [current, setCurrent] = useState<CoverageSettings | null>(null);
  const [value, setValue] = useState<AreaValue>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Allow-list — see RequireAdmin in App.tsx. Anyone who is not an administrator of
  // this county may look at the coverage boundary but not move it.
  const readOnly = !isAdmin(me?.user.role);

  const load = useCallback(async () => {
    try {
      const c = await api.coverage();
      setCurrent(c);
      setValue({ area: c.area, boundary: c.boundary, center: c.center });
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!value.boundary || !value.area) return setError('Choose a coverage area first.');
    setBusy(true);
    setError(null);
    try {
      const saved = await api.updateCoverage({ boundary: value.boundary, area: value.area });
      setCurrent(saved);
      setNotice(`Coverage area is now ${saved.area?.label ?? value.area.label}.`);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!current && !error) return <Loading label="Loading coverage area…" />;

  // Geometry counts too: a custom area keeps kind 'custom', geoid null and often the same
  // label, so comparing labels alone left Save disabled after redrawing the polygon.
  const changed =
    !!value.area &&
    (value.area.geoid !== current?.area?.geoid ||
      value.area.kind !== current?.area?.kind ||
      value.area.label !== current?.area?.label ||
      JSON.stringify(value.boundary) !== JSON.stringify(current?.boundary));

  const EXPLAINER =
    'An NWS polygon that overlaps this area raises an alert for the whole organisation. ' +
    'Assets outside it still alert on their own proximity radius. Narrowing from a county ' +
    'to a city means county-wide warnings that miss your city limits no longer notify.';

  return (
    // .page, not .page-scroll: the page itself must not scroll, so the picker can claim the
    // remaining height and hand it to the map. The control rail scrolls internally.
    <div className="page" style={{ gap: 14 }}>
      <PageHeader
        title="Coverage area"
        sub={
          current
            ? `${current.area?.label ?? `${current.countyName} (county)`} · ${current.state}`
            : undefined
        }
      >
        {!readOnly && (
          // In the header rather than under the picker: always visible, and never pushes
          // the map down the page.
          <div className="coverage-actions">
            <span className="muted coverage-note" title={EXPLAINER}>
              This area decides which alerts count as affecting you.
            </span>
            <button
              type="button"
              className="btn btn-ghost hov"
              disabled={busy || !changed}
              onClick={() => {
                setValue({ area: current!.area, boundary: current!.boundary, center: current!.center });
                setNotice(null);
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary hov"
              disabled={busy || !value.boundary || !changed}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save coverage area'}
            </button>
          </div>
        )}
      </PageHeader>
      <ErrorBox error={error} />
      {notice && <div className="ok-box">{notice}</div>}

      {current && (
        <WatchAreasPanel
          state={current.state}
          allowCounty={me?.user.role === 'super_admin'}
          readOnly={readOnly}
          coverageIsCounty={(current.area?.kind ?? 'county') === 'county'}
        />
      )}

      {readOnly ? (
        <div className="panel" style={{ padding: 16 }}>
          <div className="muted">Only an admin can change the coverage area.</div>
        </div>
      ) : (
        // No mapHeight: the picker fills this panel, and its container query splits it
        // into a control rail beside a full-height map.
        <section className="panel coverage-panel">
          <AreaPicker
            state={current?.state ?? ''}
            value={value}
            onChange={setValue}
            tenant={{
              center: current?.center ?? null,
              boundary: current?.boundary ?? null,
              timezone: me?.tenant?.timezone ?? null,
            }}
          />
        </section>
      )}
    </div>
  );
}
